import { useEffect, useRef, useState } from 'react';

import { Button, Grid } from 'antd';

import Close from '@/components/icons/Close';
import PreviewProduct from '@/components/wizard/PreviewProduct';
import { useScrollFooter } from '@/hooks/useScrollFooter';
import { cn } from '@/lib/cn';

import Wizard from '.';

type FooterProps = {
  preview?: boolean;
  closable?: boolean;
  children?: React.ReactNode;
  inline?: boolean;
  reverseButtonsOnDesktop?: boolean;
};

const Footer = ({ preview, closable, children, inline, reverseButtonsOnDesktop = true }: FooterProps) => {
  const [opened, setOpened] = useState(false);
  const screens = Grid.useBreakpoint();
  const handleRef = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useScrollFooter({
    handleRef,
    onSwipeUpFromHandle: () => setOpened(true),
    onSwipeDownOutside: () => setOpened(false),
    disabled: screens.sm || !preview
  });

  useEffect(() => {
    if (!opened) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const footer = handleRef.current;
      if (!footer) return;

      if (!footer.contains(event.target as Node)) {
        setOpened(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [opened]);

  return (
    <div
      ref={overlayRef}
      onClick={e => e.stopPropagation()}
      className={cn('transition-colors duration-300', {
        'pointer-events-none fixed top-0 left-0 z-50 h-full w-full bg-black/39': opened,
        'bg-transparent': !opened
      })}
    >
      <footer
        ref={handleRef}
        className='bg-surface-default lg:border-outline-disabled dark:border-outline-disabled dark:border-t-solid lg:border-t-solid pointer-events-auto absolute bottom-0 left-0 z-50 w-full py-0! shadow-[0px_-4px_12.9px_0px_#0000000A] lg:border-t dark:border-t'
      >
        {closable && (
          <div className='flex justify-center pt-2 pb-1 lg:hidden'>
            <span className='block h-0.75 w-10 rounded-full bg-stone-300'></span>
          </div>
        )}

        {preview && (
          <div className='relative flex h-11 items-center justify-center lg:hidden'>
            <Button
              id='preview-button'
              className='w-full underline'
              size='small'
              type='link'
              onClick={() => setOpened(previous => !previous)}
            >
              Veja como seu produto está ficando!
            </Button>

            {opened && (
              <Button
                id='close-preview-button'
                className='absolute right-3'
                size='small'
                type='text'
                onClick={() => setOpened(false)}
                icon={<Close />}
              ></Button>
            )}
          </div>
        )}

        <div
          className={cn('transform overflow-hidden transition-all duration-300 ease-in-out lg:hidden', {
            'max-h-0 opacity-0': !opened,
            'max-h-[75vh] overflow-auto opacity-100': opened
          })}
        >
          <div className='dotted-bg flex justify-center py-4'>
            <PreviewProduct />
          </div>
        </div>
        <section className='flex gap-2 px-4 py-3'>
          <Wizard.Information
            type='button'
            contentToIgnore={[
              'start',
              'chat_question_request',
              'chat_answer_request',
              'qualification_chat_question_transition'
            ]}
          />
          <div
            className={cn(
              'flex flex-1 flex-col gap-2 lg:flex-row lg:justify-end lg:border-0 lg:[&>button]:flex-none!',
              {
                'flex-row [&>button]:flex-1!': inline,
                'lg:flex-row-reverse lg:justify-start': reverseButtonsOnDesktop
              }
            )}
          >
            {children}
          </div>
        </section>
      </footer>
    </div>
  );
};

export default Footer;
